import { useMutation } from '@tanstack/react-query'
import { useId, useState } from 'react'

import { ApiError } from '@/api/client'
import { Button } from '@/components/ui/Button'
import { toast } from '@/lib/toast'

export type ExportFormat = 'csv' | 'xlsx'

interface ExportButtonProps {
  /** File name without extension -- the format picked in the select supplies that. */
  filename: string
  /** Fetches the file for the chosen format. Same shape as every other download in this
   *  app: a blob, handed to the browser as an object URL rather than a real link, because
   *  the endpoint sits behind a bearer token a plain `<a href>` cannot carry. */
  onExport: (format: ExportFormat) => Promise<Blob>
  /** Each screen already owns a `SELECT_CLASS` for its filter controls -- reuse it here
   *  rather than inventing a second style for the one extra `<select>` this adds. */
  selectClassName: string
  label?: string
  errorMessage?: string
}

/**
 * A format picker plus a download button, for the CSV/XLSX export every list screen's
 * backing router offers. One component rather than one `useMutation` per screen, since the
 * blob-to-`<a download>` dance (`InvoiceDetail`'s PDF button) is identical everywhere it's
 * used -- only the endpoint and the filename change.
 */
export function ExportButton({
  filename,
  onExport,
  selectClassName,
  label = 'Export',
  errorMessage = 'Could not export this list.',
}: ExportButtonProps) {
  const [format, setFormat] = useState<ExportFormat>('csv')
  const selectId = useId()

  const download = useMutation({
    mutationFn: async () => {
      const blob = await onExport(format)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${filename}.${format}`
      document.body.appendChild(link)
      link.click()
      link.remove()
      // The click has taken what it needs off the URL by the time this runs; freeing it
      // any sooner can race the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 0)
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : errorMessage),
  })

  return (
    <div className="flex items-center gap-2">
      <label className="sr-only" htmlFor={selectId}>
        Export format
      </label>
      <select
        id={selectId}
        className={selectClassName}
        value={format}
        onChange={(event) => setFormat(event.target.value as ExportFormat)}
      >
        <option value="csv">CSV</option>
        <option value="xlsx">XLSX</option>
      </select>
      <Button variant="secondary" loading={download.isPending} onClick={() => download.mutate()}>
        {label}
      </Button>
    </div>
  )
}
